/**
 * assets/fitment-results.js — v5
 *
 * Controller for /pages/fitment — the fitment-filtered results page.
 *
 * v5 (16 Sep 2026): REBUILT ON THE FITMENT INDEX FEED.
 *   Up to v4 this page asked Shopify to filter /collections/all by the
 *   compatible_vehicles metafield (Search & Discovery). Shopify silently
 *   switches filtering off on collections over 5,000 products, and caps
 *   filter values at ~100 vehicles per product. Once the catalogue passed
 *   5,000 the page showed the first 250 products in the shop as "parts for
 *   your car". v5 no longer uses Shopify filtering at all:
 *
 *   1. INDEX  — reads templates/collection.fitment-index.liquid, which lists
 *               every fitment-tagged product and its vehicle IDs (250 products
 *               per page, fetched in parallel, cached in localStorage 30 min).
 *   2. MATCH  — finds the active vehicle's products in the browser.
 *   3. CARDS  — fetches only the current page's cards from
 *               templates/product.fitment-card.liquid, which renders
 *               snippets/product-item.liquid, so cards look identical and
 *               price/stock stay live.
 *
 * SAFETY CHECKS (the index is rejected — error state, never wrong parts — if):
 *   - any feed page isn't valid JSON or is the wrong template version
 *   - the pages scanned don't add up to the catalogue total
 *   - any product's vehicle list came back unreadable (null)
 *   - a card response isn't a fitment card (e.g. the card template is missing)
 *
 * Kept from v4: brand / product type / price filters, sort, 48-per-page
 * pagination, universal toggle, ?vehicle=<handle> URL param, request-a-part
 * modal, chicane:badges:refresh after cards are injected.
 *
 * Depends on:
 *   window.ChicaneFitment.Garage                (fitment-garage.js)
 *   window.ChicaneFitment.vehicles              (fitment-catalog snippet)
 *   sections/fitment-results.liquid             (markup this file controls)
 *   templates/collection.fitment-index.liquid   (index feed, v2)
 *   templates/product.fitment-card.liquid       (single card renderer)
 *   assets/fitment-badge.js                     (listens for chicane:badges:refresh)
 *   snippets/cc-request-part-modal.liquid       (request-a-part modal markup)
 *
 * Exposes window.ChicaneFitment.loadProductIndex() for other scripts.
 *
 * Enable debug logs with:
 *   window.ChicaneFitmentDebug = true;
 */
(function () {
  'use strict';

  // ── Constants ─────────────────────────────────────────────────────────────────────────────
  var EVENT_CHANGED        = 'chicane:garage:changed';
  var EVENT_BADGE_REFRESH  = 'chicane:badges:refresh';
  var EVENT_GARAGE_READY   = 'chicane-garage:ready';
  var URL_PARAM_VEHICLE    = 'vehicle';
  var PAGE_SIZE            = 48;

  var INDEX_PATH           = '/collections/all';
  var INDEX_VIEW           = 'fitment-index';
  var INDEX_SORT           = 'created-ascending';
  var INDEX_VERSION        = 2;
  var INDEX_CACHE_KEY      = 'chicane-fitment-index-v2';
  var INDEX_CACHE_MAX_AGE  = 30 * 60 * 1000;   // 30 minutes
  var INDEX_FETCH_ATTEMPTS = 2;

  var CARD_VIEW            = 'fitment-card';
  var CARD_CONCURRENCY     = 8;

  var PRICE_BUCKETS = [
    { id: 'under-100',   label: 'Under $100',      min: 0,       max: 10000   },
    { id: '100-250',     label: '$100 – $250',     min: 10000,   max: 25000   },
    { id: '250-500',     label: '$250 – $500',     min: 25000,   max: 50000   },
    { id: '500-1000',    label: '$500 – $1,000',   min: 50000,   max: 100000  },
    { id: 'over-1000',   label: 'Over $1,000',     min: 100000,  max: Infinity }
  ];

  function log() {
    if (window.ChicaneFitmentDebug && window.console && console.log) {
      console.log.apply(console, ['[fitment-results]'].concat([].slice.call(arguments)));
    }
  }

  function numericId(raw) {
    if (raw === null || raw === undefined) return '';
    var m = String(raw).match(/(\d+)\s*$/);
    return m ? m[1] : '';
  }

  // ── Garage helpers ──────────────────────────────────────────────────────────────────────
  function getGarage() {
    return (window.ChicaneFitment && window.ChicaneFitment.Garage) || null;
  }
  function getActiveVehicle() {
    var g = getGarage();
    if (!g || typeof g.getActive !== 'function') return null;
    try { return g.getActive(); } catch (e) { return null; }
  }
  function getVehicleId(v) {
    if (!v) return null;
    return v.id || v.gid || v.vehicleId || null;
  }
  function getVehicleName(v) {
    if (!v) return '';
    return v.displayName || v.display || v.name || v.title || '';
  }

  // ── Catalog lookup by handle (for ?vehicle= URL param) ──────────────────
  function findVehicleByHandle(handle) {
    if (!handle) return null;
    var catalog = window.ChicaneFitment && window.ChicaneFitment.vehicles;
    if (!catalog || !catalog.length) return null;
    for (var i = 0; i < catalog.length; i++) {
      if (catalog[i].handle === handle) return catalog[i];
    }
    return null;
  }

  function applyVehicleFromUrl() {
    var params;
    try { params = new URLSearchParams(window.location.search); }
    catch (e) { return false; }

    var handle = params.get(URL_PARAM_VEHICLE);
    if (!handle) return false;

    var vehicle = findVehicleByHandle(handle);
    if (!vehicle) {
      log('?vehicle= handle not found in catalog:', handle);
      return false;
    }

    var garage = getGarage();
    if (!garage || typeof garage.addVehicle !== 'function') return false;

    var current = getActiveVehicle();
    if (current && current.id === vehicle.id) return true;

    log('Applying URL vehicle:', handle, '(', vehicle.displayName, ')');
    garage.addVehicle(vehicle.id);
    return true;
  }

  // ════════════════════════════════════════════════════════════════════════
  // PRODUCT INDEX
  // ════════════════════════════════════════════════════════════════════════

  function indexPageUrl(page) {
    return INDEX_PATH + '?view=' + INDEX_VIEW + '&sort_by=' + INDEX_SORT + '&page=' + page;
  }

  function fetchIndexPage(page) {
    return fetch(indexPageUrl(page), { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('Index page ' + page + ': HTTP ' + res.status);
      return res.text();
    }).then(function (text) {
      var data;
      try { data = JSON.parse(text); }
      catch (e) {
        var lq = text.indexOf('Liquid error');
        throw new Error('Index page ' + page + ' is not valid data' +
          (lq > -1 ? ' (' + text.slice(lq, lq + 140) + ')' : ''));
      }
      if (!data || data.v !== INDEX_VERSION || !Array.isArray(data.products)) {
        throw new Error('Index page ' + page + ' has unexpected format (v=' + (data && data.v) + ')');
      }
      return data;
    });
  }

  /**
   * Turns raw feed pages into a lookup structure, or throws if anything
   * about the pages suggests the data is incomplete or corrupt.
   * Pure function — no DOM, no network — so it can be tested in isolation.
   */
  function buildIndex(pages) {
    if (!pages.length) throw new Error('Index returned no pages');

    var expectedPages = pages[0].pages;
    var expectedItems = pages[0].items;
    if (pages.length !== expectedPages) {
      throw new Error('Index returned ' + pages.length + ' of ' + expectedPages + ' pages');
    }

    var scanned = 0;
    for (var i = 0; i < pages.length; i++) {
      if (pages[i].page !== i + 1) throw new Error('Index pages out of order at ' + (i + 1));
      if (pages[i].items !== expectedItems || pages[i].pages !== expectedPages) {
        throw new Error('Catalogue changed while loading the index');
      }
      scanned += pages[i].scanned;
    }
    if (scanned !== expectedItems) {
      throw new Error('Index scanned ' + scanned + ' of ' + expectedItems + ' products');
    }

    var products = [];
    var byVehicle = Object.create(null);
    var universal = [];
    var seen = Object.create(null);
    var links = 0;

    for (var p = 0; p < pages.length; p++) {
      var rows = pages[p].products;
      for (var r = 0; r < rows.length; r++) {
        var row = rows[r];
        if (!Array.isArray(row) || row.length < 9) {
          throw new Error('Index row has unexpected shape on page ' + (p + 1));
        }
        if (row[8] === null || !Array.isArray(row[8])) {
          throw new Error('Unreadable vehicle list for ' + row[1]);
        }
        if (seen[row[1]]) continue;   // defensive: products shifted between pages
        seen[row[1]] = true;

        var item = {
          id: String(row[0]),
          handle: row[1],
          title: row[2] || '',
          vendor: (row[3] || '').trim(),
          type: (row[4] || '').trim(),
          price: typeof row[5] === 'number' ? row[5] : null,
          available: row[6] === 1,
          universal: row[7] === 1,
          order: products.length
        };
        products.push(item);
        if (item.universal) universal.push(item);

        for (var v = 0; v < row[8].length; v++) {
          var vid = numericId(row[8][v]);
          if (!vid) continue;
          (byVehicle[vid] || (byVehicle[vid] = [])).push(item);
          links++;
        }
      }
    }

    return {
      products: products,
      byVehicle: byVehicle,
      universal: universal,
      stats: { items: expectedItems, products: products.length, universal: universal.length, links: links }
    };
  }

  /**
   * Products for a vehicle: vehicle-specific first (A–Z), then universal
   * (A–Z), deduped by handle. Pure function.
   */
  function matchProducts(index, vehicleId, includeUniversal) {
    var vid = numericId(vehicleId);
    var byTitle = function (a, b) {
      return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
    };
    var specific = (index.byVehicle[vid] || []).slice().sort(byTitle);
    if (!includeUniversal) return specific;

    var taken = Object.create(null);
    for (var i = 0; i < specific.length; i++) taken[specific[i].handle] = true;
    var extra = index.universal.filter(function (p) { return !taken[p.handle]; }).sort(byTitle);
    return specific.concat(extra);
  }

  function readIndexCache() {
    try {
      var raw = localStorage.getItem(INDEX_CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (!cached || !cached.savedAt || !Array.isArray(cached.pages)) return null;
      if (Date.now() - cached.savedAt > INDEX_CACHE_MAX_AGE) return null;
      return cached.pages;
    } catch (e) { return null; }
  }

  function writeIndexCache(pages) {
    try {
      localStorage.setItem(INDEX_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), pages: pages }));
    } catch (e) {
      log('Could not cache index (storage full?)', e);
    }
  }

  function fetchAllIndexPages() {
    return fetchIndexPage(1).then(function (first) {
      var rest = [];
      for (var p = 2; p <= first.pages; p++) rest.push(fetchIndexPage(p));
      return Promise.all(rest).then(function (others) {
        return [first].concat(others);
      });
    });
  }

  var indexPromise = null;

  function loadProductIndex(forceFresh) {
    if (indexPromise && !forceFresh) return indexPromise;

    indexPromise = (function () {
      if (!forceFresh) {
        var cachedPages = readIndexCache();
        if (cachedPages) {
          try {
            var fromCache = buildIndex(cachedPages);
            log('Index from cache:', fromCache.stats);
            return Promise.resolve(fromCache);
          } catch (e) {
            log('Cached index rejected, refetching:', e.message);
          }
        }
      }

      var attempt = 0;
      function tryFetch() {
        attempt++;
        var t0 = Date.now();
        return fetchAllIndexPages().then(function (pages) {
          var index = buildIndex(pages);
          writeIndexCache(pages);
          log('Index fetched in', Date.now() - t0, 'ms:', index.stats);
          return index;
        }).catch(function (err) {
          if (attempt < INDEX_FETCH_ATTEMPTS) {
            log('Index attempt', attempt, 'failed, retrying:', err.message);
            return tryFetch();
          }
          throw err;
        });
      }
      return tryFetch();
    })();

    indexPromise.catch(function () { indexPromise = null; });
    return indexPromise;
  }

  window.ChicaneFitment = window.ChicaneFitment || {};
  window.ChicaneFitment.loadProductIndex = loadProductIndex;
  window.ChicaneFitment._fitmentIndexInternals = { buildIndex: buildIndex, matchProducts: matchProducts };

  // ════════════════════════════════════════════════════════════════════════
  // CARDS
  // ════════════════════════════════════════════════════════════════════════

  var cardCache = Object.create(null);    // handle → Promise<Element|null>

  function fetchCard(handle) {
    if (cardCache[handle]) return cardCache[handle];

    var url = '/products/' + encodeURIComponent(handle) + '?view=' + CARD_VIEW;
    cardCache[handle] = fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (res.status === 404) return null;              // product unpublished since indexing
      if (!res.ok) throw new Error('Card ' + handle + ': HTTP ' + res.status);
      return res.text();
    }).then(function (html) {
      if (html === null) return null;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector('[data-fitment-card]');
      if (!el) throw new Error('Card ' + handle + ' did not return a fitment card');
      return document.importNode(el, true);
    });

    cardCache[handle].catch(function () { delete cardCache[handle]; });
    return cardCache[handle];
  }

  function fetchCards(handles) {
    var results = new Array(handles.length);
    var next = 0;
    function worker() {
      if (next >= handles.length) return Promise.resolve();
      var i = next++;
      return fetchCard(handles[i]).then(function (el) {
        results[i] = { handle: handles[i], el: el };
      }, function (err) {
        results[i] = { handle: handles[i], error: err };
      }).then(worker);
    }
    var workers = [];
    for (var w = 0; w < Math.min(CARD_CONCURRENCY, handles.length); w++) workers.push(worker());
    return Promise.all(workers).then(function () { return results; });
  }

  function bucketFor(priceInCents) {
    if (priceInCents === null || isNaN(priceInCents)) return null;
    for (var i = 0; i < PRICE_BUCKETS.length; i++) {
      var b = PRICE_BUCKETS[i];
      if (priceInCents >= b.min && priceInCents < b.max) return b.id;
    }
    return null;
  }

  // ════════════════════════════════════════════════════════════════════════
  // CONTROLLER
  // ════════════════════════════════════════════════════════════════════════

  function Controller(root) {
    this.root             = root;
    this.titleVehicleEl   = root.querySelector('[data-fitment-vehicle-name]');
    this.layoutEl         = root.querySelector('[data-fitment-layout]');
    this.sidebarEl        = root.querySelector('[data-fitment-sidebar]');
    this.sidebarToggleEl  = root.querySelector('[data-fitment-sidebar-toggle]');
    this.clearBtn         = root.querySelector('[data-fitment-clear]');
    this.clearFromStateBtn= root.querySelector('[data-fitment-clear-all-from-state]');
    this.activeCountEl    = root.querySelector('[data-fitment-active-count]');
    this.universalInput   = root.querySelector('[data-fitment-universal-toggle]');
    this.filterGroups     = {
      brand:       root.querySelector('[data-fitment-filter-group="brand"]'),
      productType: root.querySelector('[data-fitment-filter-group="productType"]'),
      price:       root.querySelector('[data-fitment-filter-group="price"]')
    };
    this.filterOptionContainers = {
      brand:       root.querySelector('[data-fitment-filter-options="brand"]'),
      productType: root.querySelector('[data-fitment-filter-options="productType"]'),
      price:       root.querySelector('[data-fitment-filter-options="price"]')
    };
    this.filterCountBadges = {
      brand:       root.querySelector('[data-fitment-filter-count-brand]'),
      productType: root.querySelector('[data-fitment-filter-count-productType]'),
      price:       root.querySelector('[data-fitment-filter-count-price]')
    };
    this.toolbarEl        = root.querySelector('[data-fitment-toolbar]');
    this.sortSelect       = root.querySelector('[data-fitment-sort]');
    this.countEl          = root.querySelector('[data-fitment-count]');
    this.resultsEl        = root.querySelector('[data-fitment-results]');
    this.paginationEl     = root.querySelector('[data-fitment-pagination]');
    this.stateEls         = {
      'no-garage':  root.querySelector('[data-fitment-state="no-garage"]'),
      'loading':    root.querySelector('[data-fitment-state="loading"]'),
      'empty':      root.querySelector('[data-fitment-state="empty"]'),
      'no-matches': root.querySelector('[data-fitment-state="no-matches"]'),
      'error':      root.querySelector('[data-fitment-state="error"]')
    };

    this.currentRequestId = 0;
    this.pageRequestId    = 0;
    this.allItems         = [];   // matched products (data objects), default order
    this.visibleItems     = [];   // after filters + sort
    this.activeFilters    = {
      brand:       new Set(),
      productType: new Set(),
      price:       new Set()
    };
    this.activeSort       = 'default';
    this.currentPage      = 1;

    this.wireEvents();
  }

  Controller.prototype.wireEvents = function () {
    var self = this;

    if (this.universalInput) {
      this.universalInput.addEventListener('change', function () {
        self.render();
      });
    }

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', function () {
        self.activeSort = self.sortSelect.value;
        self.currentPage = 1;
        self.applyFilters();
      });
    }

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', function () {
        self.clearAllFilters();
      });
    }

    if (this.clearFromStateBtn) {
      this.clearFromStateBtn.addEventListener('click', function () {
        self.clearAllFilters();
      });
    }

    if (this.sidebarToggleEl && this.sidebarEl) {
      this.sidebarToggleEl.addEventListener('click', function () {
        var isOpen = self.sidebarEl.getAttribute('data-open') === 'true';
        if (isOpen) {
          self.sidebarEl.removeAttribute('data-open');
          self.sidebarToggleEl.setAttribute('aria-expanded', 'false');
        } else {
          self.sidebarEl.setAttribute('data-open', 'true');
          self.sidebarToggleEl.setAttribute('aria-expanded', 'true');
        }
      });
    }

    var openBtn = this.root.querySelector('[data-fitment-open-garage]');
    if (openBtn) {
      openBtn.addEventListener('click', function () {
        var g = getGarage();
        if (g && g.UI) {
          if (typeof g.UI.open   === 'function') { g.UI.open(); return; }
          if (typeof g.UI.show   === 'function') { g.UI.show(); return; }
          if (typeof g.UI.toggle === 'function') { g.UI.toggle(); return; }
        }
        document.dispatchEvent(new CustomEvent('chicane:garage:open'));
      });
    }

    var retryBtn = this.root.querySelector('[data-fitment-retry]');
    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        self.render(true);
      });
    }

    document.addEventListener(EVENT_CHANGED, function () {
      self.render();
    });
  };

  Controller.prototype.showState = function (which) {
    for (var key in this.stateEls) {
      if (this.stateEls[key]) this.stateEls[key].hidden = true;
    }

    if (which === 'results') {
      if (this.layoutEl)  this.layoutEl.hidden  = false;
      if (this.toolbarEl) this.toolbarEl.hidden = false;
      if (this.resultsEl) this.resultsEl.hidden = false;
      if (this.sidebarEl) this.sidebarEl.style.display = '';
      if (this.sidebarToggleEl) this.sidebarToggleEl.style.display = '';
      return;
    }

    if (this.paginationEl) this.paginationEl.hidden = true;

    if (which === 'no-garage') {
      if (this.layoutEl)  this.layoutEl.hidden  = false;
      if (this.toolbarEl) this.toolbarEl.hidden = true;
      if (this.resultsEl) this.resultsEl.hidden = true;
      if (this.sidebarEl) this.sidebarEl.style.display = 'none';
      if (this.sidebarToggleEl) this.sidebarToggleEl.style.display = 'none';
      if (this.stateEls['no-garage']) this.stateEls['no-garage'].hidden = false;
      return;
    }

    if (this.layoutEl)  this.layoutEl.hidden  = false;
    if (this.sidebarEl) this.sidebarEl.style.display = '';
    if (this.sidebarToggleEl) this.sidebarToggleEl.style.display = '';

    if (which === 'loading') {
      if (this.toolbarEl) this.toolbarEl.hidden = true;
      if (this.resultsEl) this.resultsEl.hidden = true;
    } else {
      if (this.toolbarEl) this.toolbarEl.hidden = (which === 'empty' || which === 'error');
      if (this.resultsEl) this.resultsEl.hidden = true;
    }

    if (this.stateEls[which]) this.stateEls[which].hidden = false;
  };

  Controller.prototype.updateHeader = function (vehicleName) {
    if (this.titleVehicleEl) {
      this.titleVehicleEl.textContent = vehicleName || 'your car';
    }
  };

  Controller.prototype.updateCount = function (visible, total) {
    if (!this.countEl) return;
    if (typeof visible !== 'number') {
      this.countEl.innerHTML = '&nbsp;';
      return;
    }
    if (typeof total === 'number' && visible !== total) {
      this.countEl.textContent = visible + ' of ' + total + ' products';
    } else {
      this.countEl.textContent = visible + (visible === 1 ? ' product' : ' products');
    }
  };

  // ── Filters (built from index data, not DOM) ────────────────────────────
  Controller.prototype.buildFilterOptions = function () {
    var brandCounts = Object.create(null);
    var typeCounts  = Object.create(null);
    var priceCounts = Object.create(null);

    for (var i = 0; i < this.allItems.length; i++) {
      var item = this.allItems[i];
      if (item.vendor) brandCounts[item.vendor] = (brandCounts[item.vendor] || 0) + 1;
      if (item.type)   typeCounts[item.type]    = (typeCounts[item.type]    || 0) + 1;
      var bid = bucketFor(item.price);
      if (bid) priceCounts[bid] = (priceCounts[bid] || 0) + 1;
    }

    var alpha = function (a, b) { return a.toLowerCase().localeCompare(b.toLowerCase()); };

    this.renderOptionList('brand', Object.keys(brandCounts).sort(alpha).map(function (name) {
      return { value: name, label: name, count: brandCounts[name] };
    }));

    this.renderOptionList('productType', Object.keys(typeCounts).sort(alpha).map(function (name) {
      return { value: name, label: name, count: typeCounts[name] };
    }));

    var priceOpts = [];
    for (var j = 0; j < PRICE_BUCKETS.length; j++) {
      var b = PRICE_BUCKETS[j];
      if (priceCounts[b.id]) priceOpts.push({ value: b.id, label: b.label, count: priceCounts[b.id] });
    }
    this.renderOptionList('price', priceOpts);
  };

  Controller.prototype.renderOptionList = function (groupKey, options) {
    var container = this.filterOptionContainers[groupKey];
    var groupEl   = this.filterGroups[groupKey];
    if (!container || !groupEl) return;

    while (container.firstChild) container.removeChild(container.firstChild);

    var previousSelected = this.activeFilters[groupKey];
    var newSelected = new Set();

    if (options.length < 2) {
      groupEl.hidden = true;
      this.activeFilters[groupKey] = newSelected;
      return;
    }
    groupEl.hidden = false;

    var self = this;

    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      var label = document.createElement('label');
      label.className = 'cc-fitment-filter-option';

      var input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'cc-fitment-filter-option__input';
      input.value = opt.value;
      input.setAttribute('data-filter-group', groupKey);
      if (previousSelected && previousSelected.has(opt.value)) {
        input.checked = true;
        newSelected.add(opt.value);
      }

      var box = document.createElement('span');
      box.className = 'cc-fitment-filter-option__box';
      box.setAttribute('aria-hidden', 'true');

      var labelSpan = document.createElement('span');
      labelSpan.className = 'cc-fitment-filter-option__label';
      labelSpan.textContent = opt.label;

      var tally = document.createElement('span');
      tally.className = 'cc-fitment-filter-option__tally';
      tally.textContent = '(' + opt.count + ')';

      label.appendChild(input);
      label.appendChild(box);
      label.appendChild(labelSpan);
      label.appendChild(tally);
      container.appendChild(label);

      input.addEventListener('change', (function (gk, val, inp) {
        return function () {
          if (inp.checked) {
            self.activeFilters[gk].add(val);
          } else {
            self.activeFilters[gk]['delete'](val);
          }
          self.currentPage = 1;
          self.applyFilters();
        };
      })(groupKey, opt.value, input));
    }

    this.activeFilters[groupKey] = newSelected;
  };

  Controller.prototype.applyFilters = function () {
    var brandSel = this.activeFilters.brand;
    var typeSel  = this.activeFilters.productType;
    var priceSel = this.activeFilters.price;

    var filtered = this.allItems.filter(function (item) {
      if (brandSel.size > 0 && !brandSel.has(item.vendor)) return false;
      if (typeSel.size  > 0 && !typeSel.has(item.type))    return false;
      if (priceSel.size > 0) {
        var bid = bucketFor(item.price);
        if (!bid || !priceSel.has(bid)) return false;
      }
      return true;
    });

    this.visibleItems = this.sortItems(filtered);
    this.updateActiveFilterBadges();

    if (this.visibleItems.length === 0 && this.allItems.length > 0) {
      this.updateCount(0, this.allItems.length);
      this.showState('no-matches');
      return;
    }

    this.showState('results');
    this.applyPagination();
  };

  Controller.prototype.sortItems = function (items) {
    var key = this.activeSort || 'default';
    var out = items.slice();
    if (key === 'default') return out;   // already in match order

    out.sort(function (a, b) {
      if (key === 'title-asc' || key === 'title-desc') {
        var cmp = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
        return key === 'title-asc' ? cmp : -cmp;
      }
      if (key === 'price-asc' || key === 'price-desc') {
        var pa = a.price, pb = b.price;
        if (pa === null) pa = key === 'price-asc' ? Infinity : -1;
        if (pb === null) pb = key === 'price-asc' ? Infinity : -1;
        return key === 'price-asc' ? (pa - pb) : (pb - pa);
      }
      return 0;
    });
    return out;
  };

  // ── Pagination: only the current page's cards are fetched ───────────────
  Controller.prototype.applyPagination = function () {
    var total     = this.visibleItems.length;
    var pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

    if (this.currentPage > pageCount) this.currentPage = pageCount;
    if (this.currentPage < 1) this.currentPage = 1;

    var start = (this.currentPage - 1) * PAGE_SIZE;
    var slice = this.visibleItems.slice(start, start + PAGE_SIZE);

    this.updateCount(total, this.allItems.length);
    this.renderPagination(pageCount);
    this.renderPageCards(slice);
  };

  Controller.prototype.renderPageCards = function (items) {
    var self = this;
    var myId = ++this.pageRequestId;
    var handles = items.map(function (item) { return item.handle; });

    if (this.resultsEl) {
      this.resultsEl.setAttribute('aria-busy', 'true');
      this.resultsEl.style.opacity = '0.5';
    }

    fetchCards(handles).then(function (results) {
      if (myId !== self.pageRequestId) return;   // user moved on

      var failures = results.filter(function (r) { return r.error; });
      var cards    = results.filter(function (r) { return r.el; });

      if (failures.length) {
        console.warn('[fitment-results] ' + failures.length + ' card(s) failed:',
          failures.map(function (f) { return f.handle + ' — ' + f.error.message; }));
      }

      // Safety: if nothing on a non-empty page rendered, show the error state
      // rather than an empty or misleading grid.
      if (handles.length && !cards.length) {
        throw new Error('No cards could be rendered for this page');
      }

      var frag = document.createDocumentFragment();
      for (var i = 0; i < cards.length; i++) frag.appendChild(cards[i].el);

      if (self.resultsEl) {
        while (self.resultsEl.firstChild) self.resultsEl.removeChild(self.resultsEl.firstChild);
        self.resultsEl.appendChild(frag);
        self.resultsEl.removeAttribute('aria-busy');
        self.resultsEl.style.opacity = '';
      }

      try { document.dispatchEvent(new CustomEvent(EVENT_BADGE_REFRESH)); } catch (e) {}
    }).catch(function (err) {
      if (myId !== self.pageRequestId) return;
      console.error('[fitment-results] card render failed:', err);
      if (self.resultsEl) {
        self.resultsEl.removeAttribute('aria-busy');
        self.resultsEl.style.opacity = '';
      }
      self.showState('error');
    });
  };

  Controller.prototype.renderPagination = function (pageCount) {
    if (!this.paginationEl) return;

    if (pageCount <= 1) {
      this.paginationEl.hidden = true;
      this.paginationEl.innerHTML = '';
      return;
    }

    this.paginationEl.hidden = false;
    var current = this.currentPage;

    var pages = [];
    function pushPage(n) {
      if (pages.length === 0 || pages[pages.length - 1] !== n) pages.push(n);
    }
    function pushEllipsis() {
      if (pages.length === 0 || pages[pages.length - 1] !== '...') pages.push('...');
    }

    pushPage(1);

    var rangeStart = Math.max(2, current - 1);
    var rangeEnd   = Math.min(pageCount - 1, current + 1);

    if (rangeStart > 2) pushEllipsis();
    for (var p = rangeStart; p <= rangeEnd; p++) pushPage(p);
    if (rangeEnd < pageCount - 1) pushEllipsis();

    if (pageCount > 1) pushPage(pageCount);

    var self = this;
    var frag = document.createDocumentFragment();

    for (var i = 0; i < pages.length; i++) {
      var entry = pages[i];
      if (entry === '...') {
        var span = document.createElement('span');
        span.className = 'cc-fitment-pagination__ellipsis';
        span.textContent = '…';
        span.setAttribute('aria-hidden', 'true');
        frag.appendChild(span);
      } else {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cc-fitment-pagination__btn';
        btn.textContent = entry;
        btn.setAttribute('data-page', entry);
        if (entry === current) {
          btn.classList.add('is-current');
          btn.setAttribute('aria-current', 'page');
          btn.disabled = true;
        }
        btn.addEventListener('click', (function (page) {
          return function () { self.goToPage(page); };
        })(entry));
        frag.appendChild(btn);
      }
    }

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'cc-fitment-pagination__btn';
    nextBtn.setAttribute('data-page-next', '');
    nextBtn.innerHTML = 'Next <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>';
    nextBtn.style.display = 'inline-flex';
    nextBtn.style.alignItems = 'center';
    nextBtn.style.gap = '4px';
    if (current >= pageCount) {
      nextBtn.disabled = true;
    } else {
      nextBtn.addEventListener('click', function () {
        self.goToPage(self.currentPage + 1);
      });
    }
    frag.appendChild(nextBtn);

    while (this.paginationEl.firstChild) this.paginationEl.removeChild(this.paginationEl.firstChild);
    this.paginationEl.appendChild(frag);
  };

  Controller.prototype.goToPage = function (n) {
    if (n === this.currentPage) return;
    this.currentPage = n;
    this.applyPagination();

    if (this.toolbarEl) {
      this.toolbarEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (this.resultsEl) {
      this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  Controller.prototype.clearAllFilters = function () {
    this.activeFilters.brand.clear();
    this.activeFilters.productType.clear();
    this.activeFilters.price.clear();

    var inputs = this.root.querySelectorAll('[data-filter-group]');
    for (var i = 0; i < inputs.length; i++) inputs[i].checked = false;

    this.currentPage = 1;
    this.applyFilters();
  };

  Controller.prototype.updateActiveFilterBadges = function () {
    var total = 0;
    var groups = ['brand', 'productType', 'price'];
    for (var i = 0; i < groups.length; i++) {
      var k = groups[i];
      var n = this.activeFilters[k].size;
      total += n;
      var badge = this.filterCountBadges[k];
      if (badge) {
        if (n > 0) {
          badge.textContent = n;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }
    }

    if (this.clearBtn) {
      this.clearBtn.hidden = (total === 0);
    }
    if (this.activeCountEl) {
      if (total > 0) {
        this.activeCountEl.textContent = total;
        this.activeCountEl.hidden = false;
      } else {
        this.activeCountEl.hidden = true;
      }
    }
  };

  // ── Main render: vehicle → matched products ─────────────────────────────
  Controller.prototype.render = function (forceFreshIndex) {
    var self = this;
    var active = getActiveVehicle();

    if (!active) {
      this.updateHeader('your car');
      this.updateCount(null);
      this.allItems = [];
      this.visibleItems = [];
      this.currentPage = 1;
      this.showState('no-garage');
      return;
    }

    var vehicleId = numericId(getVehicleId(active));
    this.updateHeader(getVehicleName(active));

    if (!vehicleId) {
      log('Active vehicle has no ID — cannot match.');
      this.showState('error');
      return;
    }

    var includeUniversal = !!(this.universalInput && this.universalInput.checked);
    var myId = ++this.currentRequestId;
    ++this.pageRequestId;   // cancel any in-flight card page

    this.showState('loading');
    this.updateCount(null);

    loadProductIndex(forceFreshIndex === true).then(function (index) {
      if (myId !== self.currentRequestId) return;

      var matched = matchProducts(index, vehicleId, includeUniversal);
      log('Matched', matched.length, 'products for vehicle', vehicleId,
          '(specific:', (index.byVehicle[vehicleId] || []).length, ')');

      self.allItems = matched;
      self.currentPage = 1;

      if (matched.length === 0) {
        self.visibleItems = [];
        self.updateCount(0);
        self.showState('empty');
        return;
      }

      self.buildFilterOptions();
      self.applyFilters();
    }).catch(function (err) {
      if (myId !== self.currentRequestId) return;
      console.error('[fitment-results] index failed:', err);
      self.allItems = [];
      self.visibleItems = [];
      self.showState('error');
    });
  };

  // ── Request a part modal ────────────────────────────────────────────────────────────────
  function initRequestPartModal() {
    var modal = document.getElementById('cc-request-part-modal');
    if (!modal) return;

    var formWrap   = modal.querySelector('[data-cc-rqp-form-wrap]');
    var form       = modal.querySelector('[data-cc-rqp-form]')
                     || modal.querySelector('form');
    var successEl  = modal.querySelector('[data-cc-rqp-success]');
    var subjectIn  = modal.querySelector('#cc-rqp-subject');
    var vehicleIn  = modal.querySelector('#cc-rqp-vehicle');
    var vehicleHint = modal.querySelector('[data-cc-rqp-vehicle-hint]');
    var partNameIn = modal.querySelector('#cc-rqp-part-name');
    var submitBtn  = modal.querySelector('[data-cc-rqp-submit]');
    var submitLbl  = modal.querySelector('[data-cc-rqp-submit-label]');

    function open() {
      var active = getActiveVehicle();
      if (vehicleIn) {
        if (active) {
          vehicleIn.value = formatVehicleForForm(active);
          if (vehicleHint) {
            vehicleHint.textContent = 'Auto-filled from your saved garage. Edit if needed.';
          }
        } else {
          vehicleIn.value = '';
          if (vehicleHint) {
            vehicleHint.textContent = 'No vehicle saved — enter the year, make, model, and chassis if known.';
          }
        }
      }

      updateSubject();

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      if (formWrap) formWrap.hidden = false;
      if (successEl) successEl.hidden = true;

      setTimeout(function () {
        if (partNameIn) partNameIn.focus();
      }, 50);

      document.dispatchEvent(new CustomEvent('cc-request-part:opened'));
    }

    function close() {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.dispatchEvent(new CustomEvent('cc-request-part:closed'));
    }

    function formatVehicleForForm(v) {
      if (v.displayName) return v.displayName;
      var parts = [];
      if (v.year)  parts.push(v.year);
      if (v.make)  parts.push(v.make);
      if (v.model) parts.push(v.model);
      var label = parts.join(' ');
      if (v.submodel) label += ' (' + v.submodel + ')';
      return label;
    }

    function updateSubject() {
      if (!subjectIn) return;
      var partName = (partNameIn && partNameIn.value || '').trim();
      var vehicle  = (vehicleIn  && vehicleIn.value  || '').trim();
      var subject = 'Part request';
      if (partName && vehicle) {
        subject = 'Part request: ' + partName + ' for ' + vehicle;
      } else if (partName) {
        subject = 'Part request: ' + partName;
      } else if (vehicle) {
        subject = 'Part request for ' + vehicle;
      }
      subjectIn.value = subject;
    }

    function showSuccess() {
      if (formWrap) formWrap.hidden = true;
      if (successEl) successEl.hidden = false;
      try { form.reset(); } catch (e) {}
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-cc-rqp-open]');
      if (trigger) {
        e.preventDefault();
        open();
      }
    });

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-cc-rqp-close]')) {
        e.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) {
        close();
      }
    });

    if (partNameIn) partNameIn.addEventListener('input', updateSubject);
    if (vehicleIn)  vehicleIn.addEventListener('input', updateSubject);

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!submitBtn) return;

        updateSubject();

        submitBtn.disabled = true;
        var originalLabel = submitLbl ? submitLbl.textContent : 'Send request';
        if (submitLbl) submitLbl.textContent = 'Sending…';

        var formData = new FormData(form);

        // Shopify's /contact endpoint returns inconsistent statuses / redirects
        // but the email lands regardless. Any resolved fetch = success; only a
        // thrown fetch (genuine network failure) shows an error.
        fetch(form.action || '/contact', {
          method: 'POST',
          body: formData
        }).then(function () {
          showSuccess();
        }).catch(function (err) {
          console.error('[Request a part] network error:', err);
          alert('Sorry — we couldn\'t reach our servers. Please check your connection and try again, or email sales@chicaneaustralia.com.au directly.');
        }).then(function () {
          submitBtn.disabled = false;
          if (submitLbl) submitLbl.textContent = originalLabel;
        });
      });
    }

    window.CCRequestPart = {
      open: open,
      close: close
    };
  }

  // ── Boot ────────────────────────────────────────────────────────────────────────
  function boot() {
    var root = document.querySelector('[data-fitment-page]');
    if (!root) return;

    initRequestPartModal();

    // Start downloading the index straight away, in parallel with the garage
    // and vehicle catalog loading, so results appear sooner.
    loadProductIndex().catch(function () { /* surfaced by render() */ });

    var booted = false;

    function start() {
      if (booted) return;
      booted = true;

      try { applyVehicleFromUrl(); }
      catch (e) { log('applyVehicleFromUrl threw:', e); }

      var controller = new Controller(root);
      controller.render();
    }

    document.addEventListener(EVENT_GARAGE_READY, start, { once: true });

    var attempts = 0;
    var maxAttempts = 40;
    var timer = setInterval(function () {
      attempts++;
      var hasGarage  = !!getGarage();
      var hasCatalog = !!(window.ChicaneFitment && window.ChicaneFitment.vehicles && window.ChicaneFitment.vehicles.length);

      if ((hasGarage && hasCatalog) || attempts >= maxAttempts) {
        clearInterval(timer);
        start();
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
